import React from 'react';
import { useTranslation } from 'react-i18next';

import classnames from 'classnames';

import { HybridLink, Paragraph, TitleSecond } from 'common/components';
import { Langcode, RelatedContent } from 'common/interfaces/common.interfaces';
import { Navigation, PathAlias } from 'common/interfaces/menu.interfaces';

import { ContainerBackground } from 'components/Containers/ContainerBackground';

import AiMenuLinks from 'helpers/AiClusterHelper';
import BiMenuLinks from 'helpers/BiClusterHelper';
import CRMMenuLinks from 'helpers/CRMClusterHelper';
import ERPMenuLinks from 'helpers/ERPClusterHelper';
import FinanceMenuLinks from 'helpers/FinanceClusterHelper';
import HealthcareMenuLinks from 'helpers/HealthcareMenuLinks';
import InsuranceMenuLinks from 'helpers/InsuranceClusterHelper';
import MachineLearningMenuLinks from 'helpers/MLCLusterHelper';
import ManufacturingMenuLinks from 'helpers/ManufacturingClusterHelper';

import { buildMenuTree, getFilteredByLang, replaceSystemUrlsWithHumanFriendly } from './utils';

import './MoreAbout.scss';

interface Props {
  id: string;
  langcode: Langcode;
  relatedContent: RelatedContent[];
}

interface MoreAboutMenuType {
  allMenuLinkContentMachineLearning?: { nodes: Navigation[] };
  allMenuLinkContentErp?: { nodes: Navigation[] };
  allMenuLinkContentCrm?: { nodes: Navigation[] };
  allMenuLinkContentAi?: { nodes: Navigation[] };
  allMenuLinkContentBi?: { nodes: Navigation[] };
  allMenuLinkContentHealthcare?: { nodes: Navigation[] };
  allPathAliasPathAlias: { nodes: PathAlias[] };
  allMenuLinkContentFinance?: { nodes: Navigation[] };
  allMenuLinkContentInsurance?: { nodes: Navigation[] };
  allMenuLinkContentManufacturing?: { nodes: Navigation[] };
}

export const MoreAbout = ({ id, langcode = 'en', relatedContent }: Props) => {
  let MoreAboutLinks: MoreAboutMenuType;
  let title = '';
  let allMenuLinkContent;

  const { t } = useTranslation();

  const isML = id === 'machine-learning';
  const isERP = id === 'erp-services-solutions';
  const isCrm = id === 'crm-services';
  const isAi = id === 'ai-services';
  const isBi = id === 'business-intelligence-services';
  const isFinance = id === 'financial-software-development';
  const isHealthcare = id === 'healthcare-software-development';
  const isInsurance = id === 'custom-insurance';
  const isManufacturing = id === 'manufacturing';

  if (isML) {
    MoreAboutLinks = MachineLearningMenuLinks();
    title = t('More about machine learning services');
    allMenuLinkContent = MoreAboutLinks.allMenuLinkContentMachineLearning?.nodes;
  } else if (isERP) {
    MoreAboutLinks = ERPMenuLinks();
    title = t('More about ERP services');
    allMenuLinkContent = MoreAboutLinks.allMenuLinkContentErp?.nodes;
  } else if (isCrm) {
    MoreAboutLinks = CRMMenuLinks();
    title = t('More about CRM services');
    allMenuLinkContent = MoreAboutLinks.allMenuLinkContentCrm?.nodes;
  } else if (isAi) {
    MoreAboutLinks = AiMenuLinks();
    title = t('More about AI services');
    allMenuLinkContent = MoreAboutLinks.allMenuLinkContentAi?.nodes;
  } else if (isBi) {
    MoreAboutLinks = BiMenuLinks();
    title = t('More about BI services');
    allMenuLinkContent = MoreAboutLinks.allMenuLinkContentBi?.nodes;
  } else if (isFinance) {
    MoreAboutLinks = FinanceMenuLinks();
    title = t('More about financial software services');
    allMenuLinkContent = MoreAboutLinks.allMenuLinkContentFinance?.nodes;
  } else if (isHealthcare) {
    MoreAboutLinks = HealthcareMenuLinks();
    title = t('More about healthcare software services');
    allMenuLinkContent = MoreAboutLinks.allMenuLinkContentHealthcare?.nodes;
  } else if (isInsurance) {
    MoreAboutLinks = InsuranceMenuLinks();
    title = t('More about insurance software services');
    allMenuLinkContent = MoreAboutLinks.allMenuLinkContentInsurance?.nodes;
  } else if (isManufacturing) {
    MoreAboutLinks = ManufacturingMenuLinks();
    title = t('More about manufacturing software services');
    allMenuLinkContent = MoreAboutLinks.allMenuLinkContentManufacturing?.nodes;
  } else {
    throw new Error('Unknown page id');
  }

  const { nodes, paths } = getFilteredByLang(allMenuLinkContent!, MoreAboutLinks.allPathAliasPathAlias.nodes, langcode);

  const menuWithHumanReadableLinks: Navigation[] = replaceSystemUrlsWithHumanFriendly(nodes, paths);
  const menu = Array.from(buildMenuTree(menuWithHumanReadableLinks)).sort((a, b) => a[0].weight! - b[0].weight!);

  const sortedMenu = menu.map((item) =>
    item.sort(
      (a) =>
        Array.isArray(a) && (a as any).sort((c: { weight: number }, d: { weight: number }) => c.weight! - d.weight!),
    ),
  );

  const relatedContentLinks = relatedContent?.map((item) => item.path.alias);

  return (
    <ContainerBackground id="more-about" containerClass="section-padding-top-large section-padding-bottom-large">
      <TitleSecond title={title} />
      <div className="more-about-container">
        {sortedMenu.slice(1).map(([key, value]) => {
          const filteredLinks = value
            .filter((item: Navigation) => !relatedContentLinks.includes(item.link.uri))
            .sort((a, b) => a.title.localeCompare(b.title));

          return (
            <div
              className={classnames(`more-about-wrapper-list ${filteredLinks.length > 0 ? '' : 'inactive'}`)}
              key={key.id}
            >
              <Paragraph item={key.title} paragraphClass="sub-title" />
              <ul className="more-about-list">
                {filteredLinks.map((item: Navigation) => (
                  <li key={item.id}>
                    <HybridLink to={item.link.uri}>{item.title}</HybridLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </ContainerBackground>
  );
};
